import { CarrierDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Grid } from '@mui/material';
import { Autocomplete } from '../../../../../ui/components/pure/autocomplete/autocomplete.component';
import { FormControl } from '../../../../../ui/components/pure/form-control/form-control.component';
import { TextField } from '../../../../../ui/components/pure/text-field/text-field.component';
import { CourierFormFields } from '../../../components/courier-form-fields.component';
import { CourierFormModal } from '../../../components/courier-form-modal.component';
import { fullWidthStyle, rowGroupStyle } from '../../../components/courier-form.styles';
import { useCreateCourierAction } from './add-courier-action.hook';

export interface AddCourierActionProps {
  projectID: string;
  /** Fixes the carrier, in place of choosing from the project's hierarchy. */
  carrier?: Pick<CarrierDto, 'id' | 'name'>;
  onAbort(): void;
  onSuccess(): void;
}

export function CreateCourierModal(props: AddCourierActionProps) {
  const { state, handlers, form } = useCreateCourierAction(props);
  const {
    handleSubmit,
    control,
    formConfig,
    activeLoginTab,
    setActiveLoginTab,
    carrierLabel,
    unitMenuItems,
    showUnitPicker,
    loading
  } = form;

  return (
    <CourierFormModal
      title="New Courier"
      loading={loading}
      saving={state.saving}
      error={state.error}
      onCancel={handlers.onCancel}
      onSubmit={handleSubmit(handlers.onSubmit)}
    >
      <Grid
        container
        spacing={2}
        style={rowGroupStyle}
        sx={{
          alignItems: 'flex-end'
        }}
      >
        {/* The picker appears only once there is more than one unit to choose between. */}
        <Grid size={showUnitPicker ? 6 : 12}>
          {/* Shown, not chosen: display-only, and nothing may be requested against it. */}
          <TextField
            textFieldVariant="floating"
            placeholder="Carrier"
            value={carrierLabel ?? ''}
            disabled
            fullWidth
            style={fullWidthStyle}
          />
        </Grid>
        {showUnitPicker && (
          <Grid size={6}>
            {/* Type-ahead: a network-scoped operator can be offered dozens of units. */}
            <Autocomplete
              control={control}
              name={formConfig.groupID.name}
              required={formConfig.groupID.required}
              label={formConfig.groupID.label}
              placeholder={formConfig.groupID.label}
              options={unitMenuItems}
              getOptionLabel={(option) => option.label}
              loading={loading}
              fullWidth
            />
          </Grid>
        )}
      </Grid>

      <CourierFormFields
        control={control}
        projectID={props.projectID}
        formConfig={formConfig}
        activeLoginTab={activeLoginTab}
        setActiveLoginTab={setActiveLoginTab}
      />

      <Grid container spacing={2} style={rowGroupStyle}>
        <Grid size={12}>
          <FormControl
            control={control}
            name={formConfig.notes.name}
            required={formConfig.notes.required}
            Component={TextField}
            ComponentProps={{
              textFieldVariant: 'floating' as const,
              placeholder: formConfig.notes.label,
              fullWidth: true,
              multiline: true,
              style: fullWidthStyle
            }}
          />
        </Grid>
      </Grid>
    </CourierFormModal>
  );
}
